import React, { useState } from "react"
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react"
import { ExclamationTriangleIcon, QuestionMarkCircleIcon, InformationCircleIcon } from "@heroicons/react/24/outline"

export type EnkMsgDialogType = "info" | "warning" | "error" | "confirmation"

export interface IEnkMsgDialogContextType {
  messageDialog: (type: EnkMsgDialogType, message: string, buttons: string[], callback: EnkMsgDialogCallback) => void
}

export const EnkMsgDialogContext = React.createContext<IEnkMsgDialogContextType | null>(null)

export type EnkMsgDialogCallback = (result: string) => void

export default function EnkMsgDialogProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState("")
  const [dlgType, setDlgType] = useState<EnkMsgDialogType>("info")
  const [buttons, setButtons] = useState<string[]>([])

  const msgDialogCallback = React.useRef<EnkMsgDialogCallback | null>(null)

  const getDlgIcon = () => {
    //a swith statement to return the correct icon
    switch (dlgType) {
      case "confirmation":
        return (
          <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
            <QuestionMarkCircleIcon aria-hidden="true" className="h-6 w-6 text-blue-600" />
          </div>
        )

      case "warning":
        return (
          <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationTriangleIcon aria-hidden="true" className="h-6 w-6 text-yellow-600" />
          </div>
        )
      case "error":
        return (
          <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationTriangleIcon aria-hidden="true" className="h-6 w-6 text-red-600" />
          </div>
        )
      case "info":
        return (
          <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
            <InformationCircleIcon aria-hidden="true" className="h-6 w-6 text-green-500" />
          </div>
        )
    }
  }

  const getDlgTitle = () => {
    //a swith statement to return the correct title
    switch (dlgType) {
      case "confirmation":
        return "Bevestiging"
      case "warning":
        return "Waarschuwing"
      case "error":
        return "Fout"
      case "info":
        return "Informatie"
    }
  }

  const value: IEnkMsgDialogContextType = {
    messageDialog: (type: EnkMsgDialogType, message: string, buttons: string[], callback: EnkMsgDialogCallback) => {
      console.log(message)
      setDlgType(type)
      setMessage(message)
      setButtons(buttons.length > 0 ? buttons : ["OK"])
      msgDialogCallback.current = callback
      setOpen(true)
    },
  }

  return (
    <EnkMsgDialogContext.Provider value={value}>
      {children}
      <Dialog open={open} onClose={setOpen} className="relative z-10">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg data-closed:sm:translate-y-0 data-closed:sm:scale-95"
            >
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  {getDlgIcon()}
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <DialogTitle as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      {getDlgTitle()}
                    </DialogTitle>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">{message}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                {buttons.map((button, index) => {
                  return (
                    <button
                      key={index}
                      type="button"
                      data-autofocus={index === 0}
                      onClick={() => {
                        setOpen(false)
                        if (msgDialogCallback.current) {
                          msgDialogCallback.current(button)
                        }
                      }}
                      className={
                        index == 0
                          ? "inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/80 sm:ml-3 sm:w-auto"
                          : "mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      }
                    >
                      {button}
                    </button>
                  )
                })}
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </EnkMsgDialogContext.Provider>
  )
}
